import { useEffect, useState } from 'react';
import { Form, Button, Spinner, Row, Col, Card, InputGroup, Alert } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { getContents } from '../../../../../FIREBASECONFIG';
import { useResortUserMutation } from '../../../../../api/resort-booking/resort.hooks';

const ResortUserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create", // 'create', 'edit', 'view'
}) => {
  const [hotels, setHotels] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    city: '',
    phone: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const {
    createResortsUser,
    updateResortsUser,
    loading: loadingResort,
    error: errorResort,
  } = useResortUserMutation();

  useEffect(() => {
    // Fetch hotels data
    const fetchHotels = async () => {
      try {
        const data = await getContents('hotels');
        setHotels(data || []);
      } catch (error) {
        console.error("Error fetching hotels:", error);
      }
    };

    fetchHotels();
  }, []);

  // Get role options - always show all roles, don't restrict based on city
  const getRoleOptions = () => {
    return [
      { value: "Admin", label: "Admin" },
      { value: "HotelAdmin", label: "Hotel Admin" },
      { value: "ReservationAdmin", label: "Reservation Admin" },
    ];
  };

  // Check if hotel selection should be shown
  const shouldShowHotelSelection = () => {
    return formData.role === "HotelAdmin" || formData.role === "ReservationAdmin";
  };

  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "",
        city: user.city === "*" ? "all" : user.city || "",
        phone: user.phone || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
        city: "",
        phone: "",
      });
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Set city to "*" when role is Admin
      if (field === "role") {
        if (value === "Admin") {
          newData.city = "*";
        } else if (prev.city === "*" || prev.city === "all") {
          // Reset city if changing from Admin to other roles
          newData.city = "";
        }
      }

      // Handle "all" selection for city
      if (field === "city" && (value === "all" || value === "*")) {
        newData.city = "*";
      }

      return newData;
    });

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (mode === "create" && !formData.password.trim()) {
      errors.password = "Password is required";
    }

    if (mode === "create" && formData.password.trim() && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.role.trim()) {
      errors.role = "Role is required";
    }

    if (shouldShowHotelSelection() && !formData.city) {
      errors.city = "Hotel selection is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view") {
      onCancel();
      return;
    }

    if (!validateForm()) return;

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role.trim(),
        city: formData.city === "all" ? "*" : formData.city || "*",
      };

      if (mode === "create" || (mode === "edit" && formData.password.trim())) {
        payload.password = formData.password;
      }

      if (formData.phone) {
        payload.phone = formData.phone.trim();
      }

      if (mode === "create") {
        const success = await createResortsUser(payload);
        if (success) onSave && onSave(payload);
      } else if (mode === "edit" && (user?.id || user?._id)) {
        const userId = user.id || user._id;
        const success = await updateResortsUser(userId, payload);
        if (success) onSave && onSave(payload);
      }
    } catch (err) {
      console.error("Resort user mutation error:", err);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Resort User";
      case "edit":
        return "Edit Resort User";
      case "view":
        return "View Resort User Details";
      default:
        return "Resort User";
    }
  };

  const isReadOnly = mode === "view";

  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      {errorResort && (
        <Alert variant="danger" dismissible>
          {errorResort}
        </Alert>
      )}

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Name {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isInvalid={!!validationErrors.name}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!validationErrors.email}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{" "}
                {!isReadOnly && mode === "create" && (
                  <span className="text-danger">*</span>
                )}
                {mode === "edit" && (
                  <small className="text-muted"> (Leave blank to keep current)</small>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder={mode === "edit" ? "Enter new password" : "Enter password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  isInvalid={!!validationErrors.password}
                  disabled={isReadOnly}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isReadOnly}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                disabled={isReadOnly}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Module</Form.Label>
              <Form.Control type="text" value="Resort Booking" disabled />
              <Form.Text className="text-muted">
                This form is for Resort Booking users only
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Role {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                isInvalid={!!validationErrors.role}
                disabled={isReadOnly}
              >
                <option value="">Select Role</option>
                {getRoleOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.role}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Hotel {!isReadOnly && shouldShowHotelSelection() && <span className="text-danger">*</span>}
              </Form.Label>
              {formData.role === "Admin" || (!shouldShowHotelSelection() && !formData.role) ? (
                <>
                  <Form.Control type="text" value="All Hotels (*)" disabled />
                  <Form.Text className="text-muted">
                    Admin role has access to all hotels
                  </Form.Text>
                </>
              ) : (
                <>
                  <Form.Select
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    isInvalid={!!validationErrors.city}
                    disabled={isReadOnly || !formData.role}
                  >
                    <option value="">Select Hotel</option>
                    <option value="all">All Hotels</option>
                    {hotels.map((hotel) => (
                      <option key={hotel._id || hotel.id} value={hotel._id || hotel.id}>
                        {hotel.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.city}
                  </Form.Control.Feedback>
                  {!formData.role && (
                    <Form.Text className="text-muted">
                      Please select a role first
                    </Form.Text>
                  )}
                </>
              )}
            </Form.Group>
          </Col>
        </Row>

        {mode === "view" && user && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Created Date</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"
                  }
                  disabled
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  type="text"
                  value={user.isActive ? "Active" : "Inactive"}
                  disabled
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel} disabled={loadingResort}>
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button variant="primary" onClick={handleSubmit} disabled={loadingResort}>
              {loadingResort ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {mode === "create" ? "Adding..." : "Saving..."}
                </>
              ) : mode === "create" ? (
                "Add User"
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default ResortUserForm;
